import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import {
  Feather,
  BookOpen,
  Scroll,
  ArrowRight,
  Lock,
  Sparkles,
  CalendarDays,
  Flame,
  Gift,
} from "lucide-react";

const ABC_NLP_URL = "https://www.abcnlp.nl/abc-nlp-practitioner/";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/6221bdec-a1f4-4f2a-b3c7-5775bc29aacc/images/e31134d582097747674673484304c177d3bc9e13151123a0f77086a44dfae7a6.png";
const COURSE_BG =
  "https://static.prod-images.emergentagent.com/jobs/6221bdec-a1f4-4f2a-b3c7-5775bc29aacc/images/44a2095ce7cfbd93a1c01a045375d3d47e6d10d9632f64e9c1cb307d53fa1157.png";

const GRADE_ROWS = [
  { name: "Probationer", roman: "0=0" },
  { name: "Neophyte", roman: "1=10" },
  { name: "Zelator", roman: "2=9" },
  { name: "Theoricus", roman: "3=8" },
  { name: "Practicus", roman: "4=7" },
  { name: "Philosophus", roman: "5=6", crowning: true },
];

export default function LandingPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const cta = user && user !== false ? "/diary" : "/register";

  const [events, setEvents] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/events");
        if (!cancelled) setEvents(data || []);
      } catch (_) {
        if (!cancelled) setEvents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main data-testid="landing-page" className="relative">
      {/* ---------- Hero ---------- */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-40"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "multiply",
          }}
        />
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 anim-fade-up">
              <div className="label-small mb-6">{t("landing.eyebrow")}</div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-[#1A1918] mb-8">
                {t("landing.title_1")}
                <br />
                {t("landing.title_2a")}{" "}
                <span className="italic text-[#8B3A3A]">{t("landing.title_2b")}</span>{" "}
                {t("landing.title_2c")}
              </h1>
              <p className="font-body text-lg text-[#4A4238] max-w-2xl leading-relaxed mb-10">
                {t("landing.intro")}
              </p>
              <div className="flex flex-wrap items-center gap-6">
                <Link
                  to={cta}
                  className="btn-primary inline-flex items-center gap-3"
                  data-testid="landing-cta-initiate"
                >
                  {t("landing.cta_main")} <ArrowRight size={16} />
                </Link>
                {(!user || user === false) && (
                  <Link
                    to="/login"
                    className="btn-ghost"
                    data-testid="landing-cta-login"
                  >
                    {t("landing.cta_secondary")}
                  </Link>
                )}
              </div>
              {(!user || user === false) && (
                <Link
                  to="/register"
                  data-testid="landing-hero-ebook-hook"
                  className="mt-6 inline-flex items-start gap-3 border-l-2 border-[#B8860B] pl-4 py-1 max-w-xl hover:bg-[#EAE0CA]/40 transition-colors group"
                >
                  <Gift size={16} className="text-[#B8860B] mt-1 shrink-0" />
                  <div>
                    <p className="label-small text-[#B8860B] mb-1">
                      {t("landing.free_ebook_label")}
                    </p>
                    <p className="text-sm text-[#4A4238] leading-relaxed group-hover:text-[#1A1918] transition-colors">
                      {t("landing.free_ebook_body")}
                    </p>
                  </div>
                </Link>
              )}
            </div>
            <div
              className="lg:col-span-4 hidden lg:block anim-fade-up"
              style={{ animationDelay: "150ms" }}
            >
              <div className="parchment-card p-8 space-y-5" data-testid="landing-grades-card">
                <div className="label-small">{t("landing.grades_heading")}</div>
                <ol className="space-y-3 font-display text-2xl text-[#1A1918]">
                  {GRADE_ROWS.map((g) => (
                    <li
                      key={g.name}
                      className={`flex items-baseline justify-between ${
                        g.crowning ? "text-[#8B3A3A] italic" : ""
                      }`}
                    >
                      <span>{g.name}</span>
                      <span className="font-mono text-xs text-[#7A7061]">{g.roman}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Features / Benefits Grid ---------- */}
      <section
        data-testid="landing-features-section"
        className="border-t border-[#C1B49A] bg-[#EAE0CA]/30"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <header className="mb-14 max-w-3xl anim-fade-up">
            <p className="label-small mb-4">{t("landing.features_section_eyebrow")}</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#1A1918] mb-5">
              {t("landing.features_section_heading")}
            </h2>
            <p className="text-[#4A4238] leading-relaxed text-lg">
              {t("landing.features_section_intro")}
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#C1B49A]">
            <Feature
              testId="landing-feature-diary"
              icon={<Feather size={22} />}
              label={t("landing.feature_diary_label")}
              title={t("landing.feature_diary_title")}
              body={t("landing.feature_diary_body")}
              cellIdx={0}
            />
            <Feature
              testId="landing-feature-rituals"
              icon={<Scroll size={22} />}
              label={t("landing.feature_rituals_label")}
              title={t("landing.feature_rituals_title")}
              body={t("landing.feature_rituals_body")}
              cellIdx={1}
            />
            <Feature
              testId="landing-feature-lectures"
              icon={<BookOpen size={22} />}
              label={t("landing.feature_lectures_label")}
              title={t("landing.feature_lectures_title")}
              body={t("landing.feature_lectures_body")}
              cellIdx={2}
            />
            <Feature
              testId="landing-feature-pantheon"
              icon={<Sparkles size={22} />}
              label={t("landing.feature_pantheon_label")}
              title={t("landing.feature_pantheon_title")}
              body={t("landing.feature_pantheon_body")}
              cellIdx={3}
            />
            <Feature
              testId="landing-feature-library"
              icon={<Lock size={22} />}
              label={t("landing.feature_library_label")}
              title={t("landing.feature_library_title")}
              body={t("landing.feature_library_body")}
              cellIdx={4}
            />
            <Feature
              testId="landing-feature-grades"
              icon={<Flame size={22} />}
              label={t("landing.grades_heading")}
              title="Probationer → Philosophus."
              body="0=0 · 1=10 · 2=9 · 3=8 · 4=7 · 5=6"
              cellIdx={5}
              compact
            />
          </div>
        </div>
      </section>

      {/* ---------- How it works ---------- */}
      <section
        data-testid="landing-how-section"
        className="border-t border-[#C1B49A]"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <header className="mb-14 max-w-3xl anim-fade-up">
            <p className="label-small mb-4">{t("landing.how_eyebrow")}</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#1A1918]">
              {t("landing.how_heading")}
            </h2>
          </header>

          <ol className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#C1B49A]">
            {[1, 2, 3].map((n, i) => (
              <li
                key={n}
                data-testid={`landing-how-step-${n}`}
                className={`p-8 md:p-10 bg-[#F4EEDF] anim-fade-up ${
                  i < 2 ? "border-b md:border-b-0 md:border-r border-[#C1B49A]" : ""
                }`}
                style={{ animationDelay: `${i * 120}ms` }}
              >
                <p className="font-display italic text-6xl text-[#8B3A3A] leading-none mb-6">
                  {t(`landing.how_step_${n}_num`)}
                </p>
                <h3 className="font-display text-2xl text-[#1A1918] leading-tight mb-3">
                  {t(`landing.how_step_${n}_title`)}
                </h3>
                <p className="text-sm text-[#4A4238] leading-relaxed">
                  {t(`landing.how_step_${n}_body`)}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ---------- Live Work / Course ---------- */}
      <section
        data-testid="landing-course-section"
        className="relative border-t border-[#C1B49A] overflow-hidden"
      >
        <div
          className="absolute inset-0 -z-10 opacity-25"
          style={{
            backgroundImage: `url(${COURSE_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "multiply",
          }}
        />
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <header className="lg:col-span-5 anim-fade-up">
              <p className="label-small mb-4">{t("landing.course_eyebrow")}</p>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#1A1918] mb-5">
                {t("landing.course_heading")}
              </h2>
              <p className="text-[#4A4238] leading-relaxed text-lg">
                {t("landing.course_intro")}
              </p>
            </header>

            <div className="lg:col-span-7 space-y-0 border border-[#C1B49A] bg-[#F4EEDF]/85">
              <CourseBlock
                testId="landing-course-mass"
                icon={<Flame size={18} />}
                label={t("landing.course_mass_label")}
                body={t("landing.course_mass_body")}
              />
              <CourseBlock
                testId="landing-course-main"
                icon={<Sparkles size={18} />}
                label={t("landing.course_main_label")}
                body={t("landing.course_main_body")}
                emphasis
              />
              <CourseBlock
                testId="landing-course-prereq"
                icon={<Lock size={18} />}
                label={t("landing.course_prereq_label")}
                body={renderWithLink(
                  t("landing.course_prereq_body"),
                  t("landing.course_prereq_link_text"),
                  ABC_NLP_URL
                )}
                muted
              />
              <CourseBlock
                testId="landing-course-introday"
                icon={<CalendarDays size={18} />}
                label={t("landing.course_intro_day_label")}
                body={t("landing.course_intro_day_body")}
              />

              {/* Live upcoming dates pulled from /api/events */}
              <div
                className="px-6 md:px-8 py-6 bg-[#EAE0CA]/60"
                data-testid="landing-course-events"
              >
                <p className="label-small text-[#8B3A3A] mb-4 flex items-center gap-2">
                  <CalendarDays size={12} /> {t("footer.events_eyebrow")}
                </p>
                {events.length === 0 ? (
                  <p
                    className="text-sm italic text-[#7A7061]"
                    data-testid="landing-course-events-empty"
                  >
                    {t("landing.empty_events")}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {events.map((e) => (
                      <li
                        key={e.id}
                        data-testid={`landing-course-event-${e.id}`}
                        className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4 border-l-2 border-[#8B3A3A]/60 pl-3"
                      >
                        <span className="font-display text-lg text-[#1A1918] leading-tight">
                          {e.name}
                        </span>
                        <span className="text-[10px] tracking-[0.22em] uppercase text-[#8B3A3A] font-mono font-semibold mt-1 sm:mt-0">
                          {e.date_label}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Quote ---------- */}
      <section className="border-t border-[#C1B49A] bg-[#EAE0CA]/30">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-24 text-center">
          <p
            className="label-small text-[#8B3A3A] mb-6"
            data-testid="landing-quote-label"
          >
            {t("landing.quote_label")}
          </p>
          <blockquote
            className="font-display italic text-3xl sm:text-4xl md:text-5xl leading-snug text-[#1A1918]"
            data-testid="landing-quote"
          >
            {t("landing.quote")}
          </blockquote>
        </div>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section className="border-t border-[#C1B49A]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28 text-left md:text-center">
          <p
            className="label-small text-[#8B3A3A] mb-4"
            data-testid="landing-final-cta-eyebrow"
          >
            {t("landing.final_cta_eyebrow")}
          </p>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#1A1918] mb-6">
            {t("landing.final_cta_heading")}
          </h2>
          <p className="text-[#4A4238] text-lg leading-relaxed max-w-2xl md:mx-auto mb-10">
            {t("landing.final_cta_body")}
          </p>
          <div className="flex flex-wrap items-center gap-6 md:justify-center">
            <Link
              to={cta}
              className="btn-primary inline-flex items-center gap-3"
              data-testid="landing-final-cta-primary"
            >
              {t("landing.final_cta_primary")}
            </Link>
            <a
              href="#landing-course-section"
              onClick={(ev) => {
                ev.preventDefault();
                document
                  .querySelector('[data-testid="landing-course-section"]')
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="btn-ghost"
              data-testid="landing-final-cta-secondary"
            >
              {t("landing.final_cta_secondary")}
            </a>
          </div>
          {(!user || user === false) && (
            <p
              data-testid="landing-final-ebook-hook"
              className="mt-6 inline-flex items-center gap-2 text-[#B8860B] font-mono text-[11px] tracking-[0.22em] uppercase"
            >
              <Gift size={12} /> {t("landing.free_ebook_inline")}
            </p>
          )}
        </div>
      </section>
    </main>
  );
}

function Feature({ testId, icon, label, title, body, cellIdx, compact }) {
  // 3-col grid borders: right border on every non-last-in-row; bottom border on every non-last-row.
  // We don't know exact row count generically, but here we have 6 cells in a 3-col grid → 2 rows.
  const colInRow = cellIdx % 3;
  const isLastInRow = colInRow === 2;
  const isLastRow = cellIdx >= 3;
  return (
    <article
      data-testid={testId}
      className={`p-8 md:p-10 bg-[#F4EEDF] anim-fade-up ${
        !isLastInRow ? "md:border-r border-[#C1B49A]" : ""
      } ${!isLastRow ? "border-b md:border-b border-[#C1B49A]" : ""}`}
      style={{ animationDelay: `${cellIdx * 80}ms` }}
    >
      <div className="flex items-center gap-3 mb-5 text-[#8B3A3A]">
        {icon}
        <span className="label-small text-[#8B3A3A]">{label}</span>
      </div>
      <h3 className="font-display text-2xl mb-3 text-[#1A1918] leading-tight">{title}</h3>
      <p className={`${compact ? "font-mono text-xs tracking-widest text-[#7A7061]" : "text-sm text-[#4A4238] leading-relaxed"}`}>
        {body}
      </p>
    </article>
  );
}

function CourseBlock({ testId, icon, label, body, emphasis, muted }) {
  return (
    <div
      data-testid={testId}
      className={`px-6 md:px-8 py-6 border-b border-[#C1B49A] last:border-b-0 ${
        emphasis ? "bg-[#F4EEDF]" : muted ? "bg-[#EAE0CA]/40" : "bg-transparent"
      }`}
    >
      <div className="flex items-center gap-3 mb-2 text-[#8B3A3A]">
        {icon}
        <span className="label-small text-[#8B3A3A]">{label}</span>
      </div>
      <p
        className={`leading-relaxed ${
          muted
            ? "text-sm text-[#4A4238] italic"
            : emphasis
            ? "text-base text-[#1A1918]"
            : "text-sm text-[#4A4238]"
        }`}
      >
        {body}
      </p>
    </div>
  );
}

// Replace the first occurrence of `linkText` inside `text` with a real <a> to `href`.
// Returns a JSX fragment so it can be placed directly into a <p>.
function renderWithLink(text, linkText, href) {
  if (!text || !linkText) return text;
  const idx = text.indexOf(linkText);
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const after = text.slice(idx + linkText.length);
  return (
    <>
      {before}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="landing-course-prereq-link"
        className="underline decoration-[#8B3A3A]/60 underline-offset-4 hover:decoration-[#8B3A3A] hover:text-[#8B3A3A] transition-colors not-italic font-semibold"
      >
        {linkText}
      </a>
      {after}
    </>
  );
}
